<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import BaseButton from './BaseButton.vue'

interface ConfirmOptions {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  type?: 'warning' | 'danger' | 'info'
  /** 是否显示取消按钮。alert 场景可设为 false。 */
  showCancel?: boolean
  /** 是否允许点击遮罩关闭。默认 true。用于"二选一"语义场景：cancel 按钮代表一个动作而非取消时，应设为 false，避免点遮罩误触发该动作。 */
  dismissOnOverlay?: boolean
}

interface PromptOptions extends ConfirmOptions {
  inputLabel?: string
  inputValue?: string
  inputPlaceholder?: string
  inputMaxlength?: number
}

const visible = ref(false)
const options = ref<ConfirmOptions>({
  title: '确认操作',
  message: '',
  confirmText: '确定',
  cancelText: '取消',
  type: 'warning',
  showCancel: true,
  dismissOnOverlay: true
})
const promptMode = ref(false)
const promptOptions = ref<PromptOptions>({ message: '' })
const promptValue = ref('')
const promptInputRef = ref<HTMLInputElement | null>(null)

let resolvePromise: ((value: boolean) => void) | null = null
let resolvePromptPromise: ((value: string | null) => void) | null = null

/** 等待当前对话框关闭的回调，供 MessageBox.alert 在不抢占的前提下排队 */
const closeWaiters: Array<() => void> = []

/** 结算所有未完成的 Promise，避免被后续弹窗静默覆盖后永不 resolve */
function settlePendingResolvers(): void {
  if (resolvePromise) {
    const pending = resolvePromise
    resolvePromise = null
    pending(false)
  }

  if (resolvePromptPromise) {
    const pending = resolvePromptPromise
    resolvePromptPromise = null
    pending(null)
  }
}

function isShowing(): boolean {
  return visible.value
}

/** 当前对话框关闭后 resolve；未展示时立即 resolve */
function waitUntilClosed(): Promise<void> {
  if (!visible.value) {
    return Promise.resolve()
  }

  return new Promise((resolve) => {
    closeWaiters.push(resolve)
  })
}

function applyOptions(opts: ConfirmOptions): void {
  options.value = {
    title: opts.title || '确认操作',
    message: opts.message,
    confirmText: opts.confirmText || '确定',
    cancelText: opts.cancelText || '取消',
    type: opts.type || 'warning',
    showCancel: opts.showCancel !== false,
    dismissOnOverlay: opts.dismissOnOverlay !== false
  }
}

function show(opts: ConfirmOptions): Promise<boolean> {
  settlePendingResolvers()
  applyOptions(opts)
  promptMode.value = false
  visible.value = true
  return new Promise((resolve) => {
    resolvePromise = resolve
  })
}

function prompt(opts: PromptOptions): Promise<string | null> {
  settlePendingResolvers()
  applyOptions(opts)
  promptOptions.value = opts
  promptValue.value = opts.inputValue ?? ''
  promptMode.value = true
  visible.value = true
  void nextTick(() => {
    promptInputRef.value?.focus()
    promptInputRef.value?.select()
  })
  return new Promise((resolve) => {
    resolvePromptPromise = resolve
  })
}

function closeDialog(): void {
  visible.value = false
  promptMode.value = false

  // 必须拷贝：closeWaiters 是引用，直接置空会把 waiters 一起清空，导致 waiter 永不执行
  const waiters = closeWaiters.slice()
  closeWaiters.length = 0
  for (const waiter of waiters) {
    waiter()
  }
}

function handleConfirm(): void {
  closeDialog()
  if (resolvePromptPromise) {
    resolvePromptPromise(promptValue.value)
    resolvePromptPromise = null
    return
  }
  if (resolvePromise) {
    resolvePromise(true)
    resolvePromise = null
  }
}

function handleCancel(): void {
  closeDialog()
  if (resolvePromptPromise) {
    resolvePromptPromise(null)
    resolvePromptPromise = null
    return
  }
  if (resolvePromise) {
    resolvePromise(false)
    resolvePromise = null
  }
}

/**
 * 段落式长文本时才放宽宽度并左对齐，否则居中排版的内容难以阅读且会被视口截断。
 *
 * 判据用「超过 60 字」或「超过 3 行」而非「含换行」：confirmDelete 等固定文案
 * 本身就带一个换行（"确定要删除…吗？\n此操作不可恢复"），含换行即加宽会让
 * 这些短文案的确认框也变成宽屏左对齐，属于视觉回归。
 */
const isLongMessage = computed(() => {
  const message = options.value.message || ''
  return message.length > 60 || message.split('\n').length > 3
})

function handleOverlayClick(): void {
  // 二选一场景下 cancel 按钮代表一个动作（如"退出当前"），点遮罩不应触发该动作
  if (options.value.dismissOnOverlay) {
    handleCancel()
  }
}

defineExpose({ show, prompt, isShowing, waitUntilClosed })
</script>

<template>
  <Teleport to="body">
    <Transition name="confirm-dialog">
      <div v-if="visible" class="confirm-dialog-overlay" @click.self="handleOverlayClick">
        <div class="confirm-dialog" :class="{ 'confirm-dialog--wide': isLongMessage }">
          <div class="confirm-dialog-header">
            <div class="confirm-dialog-icon" :class="`confirm-dialog-icon-${options.type}`">
              <svg v-if="(options.type || 'warning') === 'warning'" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              <svg v-else-if="options.type === 'danger'" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <h3 class="confirm-dialog-title">{{ options.title }}</h3>
          </div>
          <div class="confirm-dialog-body" :class="{ 'confirm-dialog-body--long': isLongMessage }">
            <p class="confirm-dialog-message">{{ options.message }}</p>
            <label v-if="promptMode" class="confirm-dialog-input-field">
              <span v-if="promptOptions.inputLabel" class="confirm-dialog-input-label">
                {{ promptOptions.inputLabel }}
              </span>
              <input
                ref="promptInputRef"
                v-model="promptValue"
                class="confirm-dialog-input"
                type="text"
                :placeholder="promptOptions.inputPlaceholder"
                :maxlength="promptOptions.inputMaxlength"
                autocomplete="off"
                @keyup.enter="handleConfirm"
                @keyup.escape="handleCancel"
              />
            </label>
          </div>
          <div class="confirm-dialog-footer">
            <BaseButton v-if="options.showCancel !== false" variant="secondary" @click="handleCancel">
              {{ options.cancelText }}
            </BaseButton>
            <BaseButton 
              :variant="options.type === 'danger' ? 'danger' : 'primary'" 
              @click="handleConfirm"
            >
              {{ options.confirmText }}
            </BaseButton>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.confirm-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--overlay-bg);
  backdrop-filter: blur(4px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-dialog {
  background: var(--white);
  border-radius: var(--radius-lg);
  width: 90%;
  max-width: 420px;
  overflow: hidden;
  box-shadow: var(--shadow-xl);
}

.confirm-dialog-header {
  padding: var(--spacing-6) var(--spacing-6) var(--spacing-4);
  text-align: center;
}

.confirm-dialog-icon {
  width: var(--spacing-12);
  height: var(--spacing-12);
  margin: 0 auto var(--spacing-3);
  border-radius: var(--radius-full);
  display: flex;
  align-items: center;
  justify-content: center;

  &.confirm-dialog-icon-warning {
    background: var(--warning-light);
    color: var(--status-warning-fg);
  }

  &.confirm-dialog-icon-danger {
    background: var(--danger-light);
    color: var(--danger);
  }

  &.confirm-dialog-icon-info {
    background: var(--primary-50);
    color: var(--primary);
  }
}

.confirm-dialog-title {
  font-size: var(--text-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
}

.confirm-dialog--wide {
  max-width: 560px;
}

.confirm-dialog-body {
  padding: 0 var(--spacing-6) var(--spacing-6);
  text-align: center;
  max-height: 50vh;
  overflow-y: auto;
}

.confirm-dialog-body--long {
  text-align: left;
}

.confirm-dialog-message {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0;
  white-space: pre-line;
}

.confirm-dialog-input-field {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  margin-top: var(--spacing-4);
  text-align: left;
}

.confirm-dialog-input-label {
  font-size: var(--text-sm);
  color: var(--text-secondary);
}

.confirm-dialog-input {
  width: 100%;
  min-height: 40px;
  padding: 0 var(--spacing-3);
  border: 1px solid var(--border-color);
  border-radius: var(--radius);
  background: var(--white);
  color: var(--text-primary);
  font: inherit;
  outline: none;

  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 2px var(--focus-ring-info);
  }
}

.confirm-dialog-footer {
  padding: var(--spacing-4) var(--spacing-6);
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: var(--spacing-4);
  justify-content: center;

  .btn {
    min-width: 100px;
  }
}

.confirm-dialog-enter-active,
.confirm-dialog-leave-active {
  transition: all var(--transition-fast);
}

.confirm-dialog-enter-from,
.confirm-dialog-leave-to {
  opacity: 0;

  .confirm-dialog {
    transform: scale(0.95);
  }
}

.confirm-dialog-enter-to,
.confirm-dialog-leave-from {
  opacity: 1;

  .confirm-dialog {
    transform: scale(1);
  }
}
</style>
